import type { AxiosPromise } from 'axios';
// eslint-disable-next-line no-restricted-imports
import { toFetcher } from '@engage/data-fetch-v2/fetchers/toFetcher';
// eslint-disable-next-line no-restricted-imports
import { unwrap } from '@engage/data-fetch-v2/lib/unwrap';
import { userPreferencesApi } from '../singletons';

type LocationPromptParams = { acceptLanguage: string; userId: string };

type PrivacyQuestionsRequiredParams = {
  acceptLanguage: string;
  userId: string;
};
type PrivacyQuestionsOptionalParams = {
  country?: string;
  subRegion?: string;
  v2Enabled?: boolean;
};

export type UserPreferencesQuestionsResponse = {
  questions: QuestionsData[];
  length: number;
};

export type QuestionsData = {
  title: string;
  subtitle: string;
  preferenceSlugs: string[];
  orderedOptions: OrderedOptions[];
  layoutType?: 'basic' | 'bulleted_with_image';
  questionImage?: string;
  disclaimerText?: string;
  bulletedList?: BulletedListItem[];
  confirmationModal?: ConfirmationModal;
};

export type OrderedOptions = {
  slug: string;
  displayText: string;
  action?: 'dismiss' | 'submit';
};

export type BulletedListItem = {
  displayText: string;
  iconSlug: string;
};

export type ConfirmationModal = {
  slugFromOrderedOptions: string;
  title: string;
  subtitle: string;
  orderedOptions: OrderedOptions[];
};

type PrivacyLawImpactResponse = {
  userId: string;
  isUnderage: boolean;
  showPrivacyLocationModal: boolean;
  showDismissButton: boolean;
  prefilledCountrySlug: string;
  prefilledSubRegionSlug: string;
  countries: CountryInfo[];
};

export type CountryInfo = {
  displayName: string;
  slug: string;
  impacted: boolean;
  subRegionType: {
    displayName: string;
    slug: string;
  };
  subRegions: SubRegionInfo[];
};

export type SubRegionInfo = {
  displayName: string;
  slug: string;
  impacted: boolean;
};

function getPrivacyQuestions(
  api: typeof userPreferencesApi,
  acceptLanguage: string,
  userId: string,
  country: string,
  subRegion: string,
  v2Enabled?: boolean,
): AxiosPromise<UserPreferencesQuestionsResponse> {
  return api.axios.get(
    `/user_preferences/privacy_law_impact/questions?user_id=${userId}&country=${country}&sub_region=${subRegion}${
      v2Enabled !== undefined ? `&v2_enabled=${v2Enabled}` : ''
    }`,
    {
      headers: { 'Accept-Language': acceptLanguage },
    },
  );
}

function getLocationPrompt(
  api: typeof userPreferencesApi,
  acceptLanguage: string,
  userId?: string,
): AxiosPromise<PrivacyLawImpactResponse> {
  return api.axios.get(`/user_preferences/privacy_law_impact/user/${userId}`, {
    headers: { 'Accept-Language': acceptLanguage },
  });
}

function getLocationData(
  api: typeof userPreferencesApi,
  acceptLanguage: string,
  userId?: string,
): AxiosPromise<PrivacyLawImpactResponse> {
  return api.axios.get(`/user_preferences/privacy_law_impact/location/${userId}`, {
    headers: { 'Accept-Language': acceptLanguage },
  });
}

function getGenericLocationPrompt(
  api: typeof userPreferencesApi,
  acceptLanguage: string,
): AxiosPromise<PrivacyLawImpactResponse> {
  return api.axios.get(`/user_preferences/privacy_law_impact/countries`, {
    headers: { 'Accept-Language': acceptLanguage },
  });
}

export const toUserPreferencesFetchers = (
  api: typeof userPreferencesApi = userPreferencesApi,
) => ({
  getGenericLocationPrompt: toFetcher(
    () => 'GetGenericLocationPrompt',
    (params: { acceptLanguage: string }) =>
      unwrap(() => getGenericLocationPrompt(api, params.acceptLanguage)),
  ),
  GetLocationPrompt: toFetcher(
    () => 'GetLocationPrompt',
    (params: LocationPromptParams) =>
      unwrap(() => getLocationPrompt(api, params.acceptLanguage, params.userId)),
  ),
  GetLocationData: toFetcher(
    () => 'GetLocationData',
    (params: LocationPromptParams) =>
      unwrap(() => getLocationData(api, params.acceptLanguage, params.userId)),
  ),
  GetPrivacyQuestions: toFetcher(
    () => 'GetPrivacyQuestions',
    (
      reqParams: PrivacyQuestionsRequiredParams,
      optParams: PrivacyQuestionsOptionalParams,
    ) =>
      unwrap(() =>
        getPrivacyQuestions(
          api,
          reqParams.acceptLanguage,
          reqParams.userId,
          optParams?.country ?? '',
          optParams?.subRegion ?? '',
          optParams?.v2Enabled,
        ),
      ),
  ),
});
